import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatIDR, formatDate } from "./format";

const NAVY = [10, 17, 40];
const GOLD = [212, 175, 55];

export function generatePurchaseOrderPDF({ po, supplier, company, preview }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ---- Header: white background, navy text ----
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 36, "F");
  // Garis aksen gold di bawah header
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(0, 37, 210, 37);

  if (company?.logo_base64) {
    try { doc.addImage(company.logo_base64, "PNG", 14, 6, 22, 22); } catch (e) {}
  }
  const brandX = company?.logo_base64 ? 40 : 14;
  const brandW = 130 - brandX - 2;  // batas kanan header sebelum "PURCHASE ORDER"
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text((company?.company_name || "NUSANTARA ERP").toUpperCase(), brandX, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  let hy = 17;
  if (company?.address) {
    const addrLines = doc.splitTextToSize(company.address, brandW);
    doc.text(addrLines.slice(0, 2), brandX, hy);
    hy += Math.min(addrLines.length, 2) * 3.6;
  }
  const contactLine = [company?.phone && `Telp: ${company.phone}`, company?.email].filter(Boolean).join(" · ");
  if (contactLine) { doc.text(contactLine, brandX, hy); hy += 3.6; }
  if (company?.tax_id) { doc.text(`NPWP: ${company.tax_id}`, brandX, hy); hy += 3.6; }

  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("PURCHASE ORDER", 196, 18, { align: "right" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.text("Surat Pesanan Pembelian", 196, 24, { align: "right" });

  // Status watermark for special states
  const stampMap = {
    pending_approval: { text: "MENUNGGU APPROVAL", color: [234, 179, 8] },
    rejected: { text: "DITOLAK", color: [220, 38, 38] },
    received: { text: "SUDAH DITERIMA", color: [16, 185, 129] },
    cancelled: { text: "DIBATALKAN", color: [100, 116, 139] },
  };
  const stamp = stampMap[po.status];
  if (stamp) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(46);
    doc.setTextColor(...stamp.color);
    if (doc.setGState) {
      try { doc.setGState(new doc.GState({ opacity: 0.14 })); } catch (e) {}
    }
    doc.text(stamp.text, 105, 150, { align: "center", angle: -25 });
    if (doc.setGState) {
      try { doc.setGState(new doc.GState({ opacity: 1 })); } catch (e) {}
    }
  }

  // ---- Meta rows ----
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("No. PO", 14, 48);
  doc.text("Tanggal", 14, 54);
  doc.text("Status", 14, 60);
  doc.text("Dibuat Oleh", 14, 66);
  doc.text("Pembayaran", 14, 72);
  doc.setFont("helvetica", "bold");
  doc.text(po.po_number, 45, 48);
  doc.text(formatDate(po.order_date), 45, 54);
  doc.text((po.status || "").toUpperCase().replace("_", " "), 45, 60);
  doc.text(po.created_by || "-", 45, 66);
  const termLabels = { cash: "Cash / Transfer", top_7: "TOP 7 Hari", top_14: "TOP 14 Hari", top_30: "TOP 30 Hari", top_45: "TOP 45 Hari", top_60: "TOP 60 Hari" };
  doc.setTextColor(...NAVY);
  doc.text(termLabels[po.payment_term] || "TOP 30 Hari", 45, 72);
  doc.setTextColor(15, 23, 42);

  // Ship to supplier
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  doc.text("KEPADA SUPPLIER", 130, 48);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(po.supplier_name, 130, 54);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  // Alamat supplier — wrap ke beberapa baris agar tidak terpotong (lebar area kanan ~66mm)
  let supplierY = 60;
  const SUP_W = 66;  // 130 → 196 = 66mm
  if (supplier?.address) {
    const supAddrLines = doc.splitTextToSize(supplier.address, SUP_W);
    doc.text(supAddrLines.slice(0, 3), 130, supplierY);  // max 3 baris
    supplierY += Math.min(supAddrLines.length, 3) * 3.6;
  }
  if (supplier?.contact) { doc.text(`Kontak: ${supplier.contact}`, 130, supplierY); supplierY += 3.6; }
  if (supplier?.phone) { doc.text(`Telp: ${supplier.phone}`, 130, supplierY); supplierY += 3.6; }
  if (supplier?.npwp) { doc.text(`NPWP: ${supplier.npwp}`, 130, supplierY); supplierY += 3.6; }
  // Hitung tinggi maksimum antara blok kiri (meta 6 baris s/d y=72) dan kanan (supplier block)
  const tableStartY = Math.max(82, supplierY + 4);

  // ---- Items table (start dinamis) ----
  autoTable(doc, {
    startY: tableStartY,
    head: [["No", "SKU", "Nama Barang", "Qty", "Harga", "Diskon", "Subtotal"]],
    body: po.items.map((it, i) => {
      const gross = Number(it.line_gross ?? (it.quantity * it.price));
      const disc = Number(it.line_discount ?? 0);
      const net = Number(it.line_net ?? (gross - disc));
      let discLabel = "-";
      if (it.discount_type === "percent" && Number(it.discount_value) > 0) {
        discLabel = `${it.discount_value}%\n(-${formatIDR(disc)})`;
      } else if (it.discount_type === "amount" && Number(it.discount_value) > 0) {
        discLabel = `-${formatIDR(disc)}`;
      }
      return [
        String(i + 1), it.sku, it.name, String(it.quantity),
        formatIDR(it.price), discLabel, formatIDR(net),
      ];
    }),
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9, halign: "center", lineColor: NAVY, lineWidth: 0.4, fontStyle: "bold" },
    bodyStyles: { fontSize: 9, textColor: [30, 41, 59], lineColor: [30, 41, 59], lineWidth: 0.25 },
    alternateRowStyles: { fillColor: [241, 245, 249] },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      3: { halign: "right", cellWidth: 15 },
      4: { halign: "right", cellWidth: 26 },
      5: { halign: "right", cellWidth: 26, textColor: [220, 38, 38] },
      6: { halign: "right", textColor: NAVY, fontStyle: "bold" },
    },
    margin: { left: 14, right: 14 },
  });

  // ---- Totals ----
  let y = doc.lastAutoTable.finalY + 6;
  const boxX = 130;
  doc.setFontSize(9);
  const grossSubtotal = Number(po.gross_subtotal ?? po.subtotal ?? 0);
  const itemDiscTotal = Number(po.item_discount_total ?? 0);
  const poDisc = Number(po.po_discount ?? 0);
  doc.setTextColor(100, 116, 139);
  doc.text("Subtotal Bruto", boxX, y);
  doc.setTextColor(15, 23, 42);
  doc.text(formatIDR(grossSubtotal), 196, y, { align: "right" });
  y += 6;
  if (itemDiscTotal > 0) {
    doc.setTextColor(185, 28, 28);
    doc.text("(-) Diskon Item", boxX, y);
    doc.text(`-${formatIDR(itemDiscTotal)}`, 196, y, { align: "right" });
    y += 6;
  }
  if (poDisc > 0) {
    doc.setTextColor(185, 28, 28);
    const poDiscLabel = po.po_discount_type === "percent"
      ? `(-) Diskon PO (${po.po_discount_value}%)`
      : "(-) Diskon PO";
    doc.text(poDiscLabel, boxX, y);
    doc.text(`-${formatIDR(poDisc)}`, 196, y, { align: "right" });
    y += 6;
  }
  if (itemDiscTotal > 0 || poDisc > 0) {
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(boxX, y - 3, 196, y - 3);
    doc.setTextColor(100, 116, 139);
    doc.text("Subtotal Net", boxX, y);
    doc.setTextColor(15, 23, 42);
    doc.text(formatIDR(po.subtotal), 196, y, { align: "right" });
    y += 6;
  }
  if (po.include_ppn && po.ppn > 0) {
    doc.setTextColor(100, 116, 139);
    doc.text(`PPN ${po.ppn_percent || 11}%`, boxX, y);
    doc.setTextColor(15, 23, 42);
    doc.text(formatIDR(po.ppn), 196, y, { align: "right" });
    y += 6;
  } else {
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.text("PPN tidak dikenakan", boxX, y);
    y += 5;
    doc.setFontSize(9);
  }
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.5);
  doc.line(boxX, y, 196, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  // GRAND TOTAL — bingkai putih dengan border navy (bukan pill navy solid)
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.6);
  doc.roundedRect(boxX, y - 1, 66, 10, 1.5, 1.5, "FD");
  doc.setTextColor(...NAVY);
  doc.text("GRAND TOTAL", boxX + 2, y + 5.5);
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text(formatIDR(po.total), boxX + 64, y + 5.5, { align: "right" });

  // ---- Notes ----
  if (po.notes) {
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text("Catatan:", 14, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(po.notes, 14, y + 5, { maxWidth: 180 });
  }

  // ---- Signature blocks (4 kolom: Dibuat, Diperiksa, Disetujui, Supplier) ----
  const sigY = Math.max(doc.lastAutoTable.finalY + 55, 200);
  const PW = 210;
  const M = 14;
  const boxW = 42;
  const gap = (PW - 2 * M - 4 * boxW) / 3; // gap merata di antara 4 kotak
  const boxes = [
    { x: M, label: "Dibuat oleh", sub: "(Purchasing)", role: po.created_by || "" },
    { x: M + boxW + gap, label: "Diperiksa oleh", sub: "(Finance / Manager)", role: "" },
    { x: M + 2 * (boxW + gap), label: "Disetujui oleh", sub: "(Owner)", role: po.approved_by || "" },
    { x: M + 3 * (boxW + gap), label: "Approval Supplier", sub: `(${(po.supplier_name || "Supplier").slice(0, 22)})`, role: "" },
  ];

  // Header bar navy tipis di atas tiap kotak
  boxes.forEach((b) => {
    doc.setFillColor(...NAVY);
    doc.rect(b.x, sigY - 4, boxW, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(b.label.toUpperCase(), b.x + boxW / 2, sigY + 0.7, { align: "center" });

    // Kotak bawah untuk ttd
    doc.setDrawColor(...NAVY);
    doc.setLineWidth(0.4);
    doc.rect(b.x, sigY + 3, boxW, 28);

    // Sub-label
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(b.sub, b.x + boxW / 2, sigY + 8, { align: "center" });

    // Baris ttd
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(b.x + 3, sigY + 25, b.x + boxW - 3, sigY + 25);

    // Nama (bila ada)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(15, 23, 42);
    if (b.role) doc.text(b.role.slice(0, 20), b.x + boxW / 2, sigY + 24, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(120, 120, 120);
    doc.text("Nama & Tanda Tangan", b.x + boxW / 2, sigY + 29.5, { align: "center" });
  });

  // ---- Terms & Conditions (SETELAH tanda tangan) ----
  const terms = (company?.po_terms_conditions || "").trim();
  if (terms) {
    let ty = sigY + 38;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text("SYARAT & KETENTUAN", 14, ty);
    doc.setDrawColor(...GOLD);
    doc.line(14, ty + 2, 80, ty + 2);
    ty += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(terms, 182);
    doc.text(lines, 14, ty);
  }

  // Footer
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(148, 163, 184);
  doc.text(`Diterbitkan oleh ${company?.company_name || "FUJI INDO SURYA ERP SYSTEM"} · Rangkap: [ ] Arsip Pembelian [ ] Gudang [ ] Supplier [ ] Finance`, 14, 289);

  const filename = `${po.po_number.replaceAll("/", "-")}.pdf`;
  if (arguments[0]?.preview) return { url: doc.output("bloburl"), filename };
  doc.save(filename);
}
